import React, { useState } from 'react'
import { Icon } from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import {
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import type { RunLOCalculationRequest } from '@matech/thebigpos-sdk'
import { AxiosError } from 'axios'
import { useIntl } from 'react-intl'
import LoanCalculatorForm, {
	LoanCalculatorFormValues,
} from '../forms/LoanCalculatorForm'
import { TheBigPOSApi } from '../../../../lib/TheBigPOSClient'
import { eventTypes } from '../../../../services/constants'
import useUser from '../../../../hooks/useUser'
import { useMixpanel } from '../../../../hooks/useMixpanel'
import { useLoanContext } from '../../../../contexts/LoanContext'
import ErrorList, {
	ErrorListItemProps,
} from '../../../../components/ErrorList'
import { LayoutRow } from '../../../../components/layout'
import queryKeys from '../../../../services/queryKeys'
import { ModalPleaseWait } from '../../../../components/modals/ModalPleaseWait'
import { useAlert } from '../../../../hooks'

const LoanCalculatorTemplate = () => {
	const { isLoanAdmin } = useUser()
	const { loan, isLoading, isRefetching } = useLoanContext()
	const loanId = loan?.loanID || ''
	const queryClient = useQueryClient()
	const mixpanel = useMixpanel()
	const { formatMessage } = useIntl()
	const { alert } = useAlert()

	const [apiErrors, setApiErrors] = useState<
		ErrorListItemProps[] | null
	>(null)
	const [loadingMessage, setLoadingMessage] = useState('')

	const {
		error,
		data: loanCalculatorResult,
		isFetching,
	} = useQuery({
		queryKey: [queryKeys.loanCalculator, { loanId }],
		queryFn: () => TheBigPOSApi.getLoanCalculator(loanId as string),
		enabled: !!loanId && loanId !== '',
	})

	const invalidateQueries = async () => {
		await queryClient.invalidateQueries({
			queryKey: [queryKeys.loanCalculator],
		})
	}

	const loanCalculatorResultData = loanCalculatorResult?.data

	const loanLocked =
		loanCalculatorResultData?.loanLocked || loan?.isLocked

	const runLoanCalculatorMutation = useMutation({
		mutationFn: (data: RunLOCalculationRequest) =>
			TheBigPOSApi.runLoanCalculator(loanId as string, data),
	})

	const loading =
		isLoading ||
		isRefetching ||
		isFetching ||
		runLoanCalculatorMutation.isPending

	const handleLoanCalculatorSubmit = async (
		data: LoanCalculatorFormValues
	) => {
		try {
			setLoadingMessage(`Updating & Retrieving calculations`)

			await runLoanCalculatorMutation.mutateAsync(data)

			mixpanel.trackEvent(eventTypes.CALCULATE_LOAN_PAYMENT, {
				loanId,
			})

			await invalidateQueries()
		} catch (e) {
			if (e instanceof AxiosError) {
				if (e.response && e.response.status === 422) {
					setApiErrors(e.response.data.errors)
				}
			}
		}
	}

	if (error) {
		alert(formatMessage({ id: 'errors.error_loading' }), {
			severity: 'error',
		})
	}

	return (
		<>
			<div className="flex flex-col md:flex-row">
				<div className="flex flex-col grow">
					<div className="flex flex-row items-center mb-4">
						{!isLoanAdmin && (
							<p className="text-xl font-rubik font-bold mr-2 dark:text-white">
								Adjustments
							</p>
						)}
						{loan?.isLocked && (
							<Icon
								aria-label="upload"
								sx={{ height: 40, width: 24 }}
							>
								<LockOutlinedIcon />
							</Icon>
						)}
					</div>
					{apiErrors && (
						<LayoutRow>
							<ErrorList errors={apiErrors} />
						</LayoutRow>
					)}
					{loanId && (
						<LoanCalculatorForm
							loanId={loanId}
							onSubmit={handleLoanCalculatorSubmit}
							loanLocked={loanLocked}
							loading={loading}
							defaultValues={
								loanCalculatorResultData as LoanCalculatorFormValues
							}
						/>
					)}
				</div>
			</div>
			<ModalPleaseWait
				visible={runLoanCalculatorMutation.isPending}
				message={loadingMessage}
			/>
		</>
	)
}

export default LoanCalculatorTemplate
